/*
 * V1.1.4 — derive the single active In-Progress payroll run from the
 * authenticated user's payroll list.
 *
 * Prototype anchor: `renderResumePayrollPill()` /
 * `_hasActiveInProgressRun()` in Optimal-HR-Payroll-Module.html (line
 * 5368+). In the prototype the pill is wired up to local state; here it
 * subscribes to the same list TanStack Query already populates for the
 * Payroll Center landing, so the pill stays in sync without a second
 * round-trip.
 *
 * History: prior to sibling-repo PR #47 (PayrollId in PayrollResponse),
 * this module exposed a `payrollResumeKey()` helper that folded a
 * composite (cycle, template, pay-date) into an opaque route param.
 * The composite is retired now — the route param IS the backend
 * PayrollId.
 */
import { computed, type ComputedRef } from 'vue';
import type { MaybeRefOrGetter } from 'vue';
import type { Payroll } from '@/api/services/payroll';
import { usePayrolls } from './usePayrolls';

export interface UseActivePayrollRunOptions {
  /**
   * Gate the underlying `/payrolls` fetch. The top-bar pill passes a
   * route-scoped flag so the list is only fetched inside the Payroll module,
   * not on Home after sign-in. Defaults to enabled.
   */
  enabled?: MaybeRefOrGetter<boolean>;
}

/**
 * Pick the single active In-Progress run. If multiple are present (rare,
 * but the schema allows it during an off-cycle overlap), prefer the
 * most-recent PayDate so the pill points at the user's freshest work.
 */
export function pickActiveRun(payrolls: Payroll[] | undefined): Payroll | null {
  if (!payrolls?.length) return null;
  const inProgress = payrolls.filter((p) => p.Status === 'IN_PROGRESS');
  if (inProgress.length === 0) return null;
  if (inProgress.length === 1) return inProgress[0] ?? null;
  return (
    [...inProgress].sort((a, b) => (b.PayDate ?? '').localeCompare(a.PayDate ?? ''))[0] ?? null
  );
}

export interface UseActivePayrollRunReturn {
  /** The active In-Progress payroll, or null when none. */
  activeRun: ComputedRef<Payroll | null>;
  /** The active run's PayrollId (used as the `:payrollId` route param). */
  payrollId: ComputedRef<string | null>;
  /** Mirrors the underlying `usePayrolls` query state. */
  isLoading: ComputedRef<boolean>;
  isError: ComputedRef<boolean>;
}

export function useActivePayrollRun(
  options: UseActivePayrollRunOptions = {},
): UseActivePayrollRunReturn {
  const { query } = usePayrolls({ enabled: options.enabled });
  const activeRun = computed<Payroll | null>(() => pickActiveRun(query.data.value));
  const payrollId = computed<string | null>(() => activeRun.value?.PayrollId ?? null);
  const isLoading = computed(() => query.isLoading.value);
  const isError = computed(() => query.isError.value);
  return { activeRun, payrollId, isLoading, isError };
}
